import {
  GridColDef,
  GridFilterOperator,
  getGridSingleSelectOperators,
  getGridStringOperators
} from '@mui/x-data-grid-pro';
import { termLabel } from '../../../utils/term';

// Leaf module (mui-only imports) so UpstackDataGrid and the report grid can share it without
// entering the helpers/store/col-defs import cycle.

type SanitizerColumn = Pick<GridColDef, 'field' | 'type' | 'filterOperators'>;

type FilterModelItem = { field: string; operator: string; value?: unknown };

export interface PartitionedFilterModelItems<T> {
  // What the current column set can show.
  items: T[];
  // Held back only by a column that is not there, which a later fetch can still supply.
  awaitingColumn: T[];
}

// A column whose filter offers the option-list operators can no longer match a raw two-state
// value; one left on the boolean operator still can.
const filtersByOptionList = (column?: SanitizerColumn): boolean =>
  (column?.filterOperators || []).some((operator) => operator.value === 'isAnyOf');

// String and singleSelect resolve to the list MUI would apply; anything else is left to MUI to judge.
const operatorsFor = (column: SanitizerColumn): readonly GridFilterOperator[] | undefined => {
  if (column.filterOperators) return column.filterOperators;
  if (column.type === 'singleSelect') return getGridSingleSelectOperators();

  return column.type === undefined || column.type === 'string' ? getGridStringOperators() : undefined;
};

// A view saved before a column's operator set changed still names the old operator, and MUI throws on it.
const offersOperator = (column: SanitizerColumn, operator: string): boolean => {
  const operators = operatorsFor(column);

  return !operators || operators.some((candidate) => candidate.value === operator);
};

// `false` meant "any account name", so no two-state value maps onto one of today's options.
const keepsOriginValue = (item: FilterModelItem, column?: SanitizerColumn): boolean => {
  const twoStateValue = typeof item.value === 'boolean' || item.value === 'true' || item.value === 'false';

  return !(twoStateValue && filtersByOptionList(column));
};

// A term saved before the column resolved to a display label still holds a raw spelling; map it
// forward so the saved view keeps matching instead of silently going empty.
const migrateTermValue = <T extends { field: string; value?: unknown }>(item: T): T => {
  if (item.field !== 'term') return item;
  if (Array.isArray(item.value)) return { ...item, value: item.value.map((entry) => termLabel(entry as string)) };
  if (typeof item.value !== 'string') return item;

  return { ...item, value: termLabel(item.value) };
};

// Splits a saved filter model into what the grid can show and what a stale saved item has earned. An
// undefined column set is "not known yet" and judges nothing; an empty one is an answer, and drops what it
// cannot place. A field missing from a set that is known is reported apart from the stale items, because a
// column arriving late must cost the user nothing, while an operator the column refuses is gone for good.
export const partitionFilterModelItems = <T extends FilterModelItem>(
  items: T[],
  columns: SanitizerColumn[] | undefined
): PartitionedFilterModelItems<T> => {
  const columnsByField = columns && new Map(columns.map((col) => [col.field, col]));
  const kept: T[] = [];
  const awaitingColumn: T[] = [];

  items.map(migrateTermValue).forEach((item) => {
    const column = columnsByField?.get(item.field);
    if (columnsByField && !column) return void awaitingColumn.push(item);
    if (column && !offersOperator(column, item.operator)) return;
    if (item.field === 'origin' && !keepsOriginValue(item, column)) return;

    kept.push(item);
  });

  return { items: kept, awaitingColumn };
};

// Drops saved filter items that would crash GridFilterForm or silently match nothing.
export const sanitizeFilterModelItems = <T extends FilterModelItem>(
  items: T[],
  columns: SanitizerColumn[] | undefined
): T[] => partitionFilterModelItems(items, columns).items;
